/* FILE: ./frontend/src/features/canada/pages/SmartListingForm.jsx */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useCompliance } from '../../../hooks/useCompliance';
import SupplierOnboarding from './SupplierOnboarding';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation, Trans } from 'react-i18next';

// ⚡ CORE & SHELL MANDATE: Allowed HS Chapters
const CONSTRUCTION_CHAPTERS = ['25', '27', '32', '39', '44', '68', '69', '70', '72', '73', '74', '76', '83'];

// ⚡ THE SYNONYM DICTIONARY (For Smart Autocomplete)
const TRADE_SYNONYMS = [
    { keywords: ['beam', 'h-beam', 'i-beam', 'structural steel', 'girder'], code: '7216.32', description: 'Structural Steel H/I/U Beams & Sections' },
    { keywords: ['pipe', 'tube', 'tubing', 'hollow'], code: '7304.00', description: 'Steel Pipes, Tubes and Hollow Profiles' },
    { keywords: ['lumber', 'wood', 'timber', 'board', 'plywood'], code: '4407.11', description: 'Sawn Wood, Lumber & Timber (Coniferous)' },
    { keywords: ['wire', 'cable', 'electrical'], code: '8544.11', description: 'Insulated Copper Wire & Electrical Cables' },
    { keywords: ['cement', 'concrete', 'mortar'], code: '2523.29', description: 'Portland Cement & Hydraulic Cements' },
    { keywords: ['drywall', 'gypsum', 'plasterboard'], code: '6809.11', description: 'Plasterboards & Drywall Panels' }
];

export default function SmartListingForm({ onCancel, initialData = null }) {
  const navigate = useNavigate();
  const { checkCompliance, loading: rulesLoading } = useCompliance();
  const { user, userProfile } = useAuth();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [supplierId, setSupplierId] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  // ⚡ AUTOCOMPLETE STATE
  const dropdownRef = useRef(null);
  const [hsSearchTerm, setHsSearchTerm] = useState(initialData?.hs_code || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearchingDb, setIsSearchingDb] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  // ⚡ PRE-FLIGHT CHECKER STATE (Education UI)
  const [preFlightWarning, setPreFlightWarning] = useState(null);

  // ENTRY MODE STATE (Single vs Bulk)
  const [entryMode, setEntryMode] = useState('single');

  // SUBSCRIPTION TIER LOGIC (Epic #3 Limits)
  const isPremium = userProfile?.supplier_tier === 'premium' || userProfile?.role === 'admin';
  const MAX_IMAGES = isPremium ? 5 : 3;
  const MAX_PDFS = isPremium ? 10 : 1;
  const MAX_CAD = isPremium ? 2 : 0;

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    hsCode: initialData?.hs_code || '',
    sku: initialData?.sku || '', 
    price_cad: initialData?.price_cad || '',
    uom: initialData?.uom || 'Each (EA)', // ⚡ FIX: Updated Default Fallback
    custom_uom: '', 
    moq: initialData?.moq || '1', 
    lead_time_days: initialData?.lead_time_days || '0', 
    stock_status: initialData?.stock_status || 'in_stock', 
    origin_province: initialData?.origin_province || 'ON',
    description: initialData?.description || '',
    is_civilian_compliant: !!initialData, 
    is_safety_compliant: !!initialData,
    video_url: initialData?.video_url || '', 
    material_grade: initialData?.material_grade || '',
    brand: initialData?.brand || '',
    condition: initialData?.condition || 'New',
    weight_kg: initialData?.weight_kg || '',
    dimensions: initialData?.dimensions || ''
  });

  const [isCustomUom, setIsCustomUom] = useState(false);
  const [complianceStatus, setComplianceStatus] = useState({ restricted: false, reason: '' });
  const [certifications, setCertifications] = useState(
      initialData?.certification_claimed && initialData.certification_claimed !== 'None / Standard'
      ? initialData.certification_claimed.split(',').map(c => c.trim())
      : []
  );
  const [certInput, setCertInput] = useState('');

  // FILE STORAGE STATES
  const [images, setImages] = useState(initialData?.image_url ? initialData.image_url.split(',') : []);
  const [pdfs, setPdfs] = useState(initialData?.technical_docs_url ? initialData.technical_docs_url.split(',') : []);
  const [cads, setCads] = useState(initialData?.cad_url ? initialData.cad_url.split(',') : []);

  // COMPLIANCE DOCUMENT HOLD STATE
  const [complianceHold, setComplianceHold] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const [certFile, setCertFile] = useState(null);
  const [uploadingCert, setUploadingCert] = useState(false);

  // BULK CSV STATE
  const [csvRows, setCsvRows] = useState([]);
  const [csvValidated, setCsvValidated] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) {
        setStatusMessage('Please sign in to list products.');
        setLoading(false);
        return;
      }
      setCurrentUserId(authUser.id);

      const { data: supplier } = await supabase
        .from('suppliers')
        .select('id')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (supplier) setSupplierId(supplier.id);
      
      if (initialData?.hs_code) {
        runPreFlightCheck(initialData.hs_code);
      }
      
      setLoading(false);
    };
    init();
  }, [initialData]);

  useEffect(() => {
    if (!rulesLoading) {
      const result = checkCompliance(
        formData.name + ' ' + formData.description + ' ' + formData.material_grade, 
        formData.hsCode 
      );
      setComplianceStatus(result);
    }
  }, [formData.hsCode, formData.name, formData.description, formData.material_grade, rulesLoading, checkCompliance]);

  const resetHold = () => {
      if (complianceHold) {
          setComplianceHold(false);
          setHoldReason('');
          setCertFile(null);
      }
  };

  useEffect(() => {
    if (!hsSearchTerm || hsSearchTerm.length < 2 || hsSearchTerm === formData.hsCode) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
    }

    const fetchSuggestions = async () => {
        setIsSearchingDb(true);
        try {
            const queryLower = hsSearchTerm.toLowerCase();
            let allMatches = [];

            TRADE_SYNONYMS.forEach(item => {
                if (item.keywords.some(kw => queryLower.includes(kw))) {
                    allMatches.push({ code: item.code, description: item.description });
                }
            });

            const { data: dbSynonyms } = await supabase
                .from('hs_synonyms')
                .select('colloquial_term, target_hs_code')
                .ilike('colloquial_term', `%${queryLower}%`)
                .limit(5);

            if (dbSynonyms) {
                dbSynonyms.forEach(s => {
                    allMatches.push({ code: s.target_hs_code, description: s.colloquial_term });
                });
            }

            let query = supabase.from('hs_codes').select('code, description');
            const numericSearch = hsSearchTerm.replace(/[^0-9]/g, '');
            
            if (numericSearch.length >= 2) {
                query = query.ilike('code', `${numericSearch}%`);
            } else {
                const words = hsSearchTerm.trim().split(/\s+/).filter(w => w.length > 2);
                const chapterFilter = CONSTRUCTION_CHAPTERS.map(ch => `code.ilike.${ch}%`).join(',');
                
                query = query.or(chapterFilter);
                words.forEach(w => { query = query.ilike('description', `%${w}%`); });
            }

            const { data: officialCodes, error } = await query.limit(5);
            if (error) throw error;
            
            const combined = [...allMatches, ...(officialCodes || [])];
            const unique = Array.from(new Map(combined.map(item => [item.code, item])).values());

            setSuggestions(unique.slice(0, 6));
            setShowDropdown(unique.length > 0);

        } catch (err) {
            console.error("Autocomplete Error:", err);
        } finally {
            setIsSearchingDb(false);
        }
    };

    if (hsSearchTerm.includes('(') && hsSearchTerm.includes(')')) return;

    const delayDebounceFn = setTimeout(() => { fetchSuggestions(); }, 300); 
    return () => clearTimeout(delayDebounceFn);
  }, [hsSearchTerm, formData.hsCode]);

  useEffect(() => {
    const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setShowDropdown(false);
        }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownRef]);

  const runPreFlightCheck = (code) => {
    if (code.length >= 2) {
      const chapter = code.substring(0, 2);
      if (!CONSTRUCTION_CHAPTERS.includes(chapter)) {
          setPreFlightWarning({
              chapter: chapter,
              title: t('inventory.preflight_title'),
              message: t('inventory.preflight_desc'),
              detail: t('inventory.preflight_detail', { chapter })
          });
      } else {
          setPreFlightWarning(null);
      }
    } else {
      setPreFlightWarning(null);
    }
  };

  const handleSuggestionClick = (item) => {
      setHsSearchTerm(`${item.description} (${item.code})`);
      setFormData(prev => ({ ...prev, hsCode: item.code }));
      setShowDropdown(false);
      resetHold();
      runPreFlightCheck(item.code);
  };

  const handleHsSearchChange = (e) => {
    const val = e.target.value;
    setHsSearchTerm(val);
    
    const numericCode = val.replace(/[^0-9.]/g, '').slice(0, 12);
    setFormData(prev => ({ ...prev, hsCode: numericCode }));
    resetHold();
    runPreFlightCheck(numericCode);
  };

  const handleDescriptionChange = (e) => {
    setFormData({...formData, description: e.target.value});
    resetHold();
  };

  const handleCancel = () => {
    if (onCancel && typeof onCancel === 'function') {
        onCancel();
    } else {
        navigate(-1);
    }
  };

  const handleUomChange = (e) => {
    const val = e.target.value;
    if (val === 'Other') {
        setIsCustomUom(true);
        setFormData(prev => ({ ...prev, uom: 'Other', custom_uom: '' }));
    } else {
        setIsCustomUom(false);
        setFormData(prev => ({ ...prev, uom: val }));
    }
  };

  const handleAddCert = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
        e.preventDefault();
        const newCert = certInput.trim();
        if (newCert && !certifications.includes(newCert)) {
            setCertifications(prev => [...prev, newCert]);
        }
        setCertInput('');
    }
  };

  const handleRemoveCert = (certToRemove) => {
      setCertifications(prev => prev.filter(c => c !== certToRemove));
  };

  const handleIntegerChange = (e, field) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length > 1 && val.startsWith('0')) {
        val = val.replace(/^0+/, '');
    }
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  const handlePriceChange = (e) => {
    let val = e.target.value;
    if (val === '' || /^\d+(\.\d{0,2})?$/.test(val)) {
        if (val.length > 1 && val.startsWith('0') && val[1] !== '.') {
             val = val.replace(/^0+/, '');
        }
        setFormData(prev => ({ ...prev, price_cad: val }));
    }
  };

  const blockInvalidChar = e => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

  const handleFileSelect = (e) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          if (file.type !== 'application/pdf') {
              alert("Only PDF documents are accepted for compliance certificates.");
              e.target.value = null; 
              return;
          }
          if (file.size > 5 * 1024 * 1024) { 
              alert("File is too large. Maximum size is 5MB.");
              e.target.value = null;
              return;
          }
          setCertFile(file);
      }
  };

  const uploadCertificate = async () => {
      if (!certFile) return null;
      setUploadingCert(true);
      try {
          const fileExt = certFile.name.split('.').pop();
          const fileName = `${supplierId}_${Date.now()}.${fileExt}`;
          const filePath = `${supplierId}/${fileName}`; 

          const { error: uploadError } = await supabase.storage
              .from('compliance-certs')
              .upload(filePath, certFile);
          if (uploadError) throw uploadError;

          const { data } = supabase.storage.from('compliance-certs').getPublicUrl(filePath);
          return data.publicUrl;
      } catch (err) {
          console.error("Storage Upload Error:", err);
          throw new Error("Failed to upload certificate document.");
      } finally {
          setUploadingCert(false);
      }
  };

  const handleFileDrop = (e, type) => {
      const files = Array.from(e.target.files);
      if (type === 'image') {
          if (images.length + files.length > MAX_IMAGES) return alert(`Limit Reached: Standard tier allows ${MAX_IMAGES} images. Upgrade to Pro for more.`);
          setImages([...images, ...files]);
      } else if (type === 'pdf') {
          if (pdfs.length + files.length > MAX_PDFS) return alert(`Limit Reached: Standard tier allows ${MAX_PDFS} PDF docs. Upgrade to Pro for more.`);
          setPdfs([...pdfs, ...files]);
      } else if (type === 'cad') {
          if (!isPremium) return alert("Upgrade Required: CAD & BIM file hosting is a Premium feature.");
          if (cads.length + files.length > MAX_CAD) return alert(`Limit Reached: Premium tier allows ${MAX_CAD} CAD models per SKU.`);
          
          const oversized = files.find(f => f.size > 50 * 1024 * 1024);
          if (oversized) return alert("File too large: CAD models must be under 50MB.");
          setCads([...cads, ...files]);
      }
      e.target.value = null; 
  };

  const removeFile = (index, type) => {
      if (type === 'image') setImages(images.filter((_, i) => i !== index));
      if (type === 'pdf') setPdfs(pdfs.filter((_, i) => i !== index));
      if (type === 'cad') setCads(cads.filter((_, i) => i !== index));
  };

  const uploadFilesToSupabase = async (filesArray, bucketName) => {
      const uploadedUrls = [];
      for (const file of filesArray) {
          if (typeof file === 'string') { 
              uploadedUrls.push(file); 
              continue; 
          }
          const fileName = `${supplierId}_${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
          const { error } = await supabase.storage.from(bucketName).upload(fileName, file);
          if (!error) {
              const { data } = supabase.storage.from(bucketName).getPublicUrl(fileName);
              uploadedUrls.push(data.publicUrl);
          }
      }
      return uploadedUrls.join(',');
  };

  const downloadCsvTemplate = () => {
      const headers = ["Name", "HS_Code", "SKU", "Price", "UOM", "MOQ", "Description", "Material", "Brand"];
      const sampleRow = [
          "Structural Steel H-Beam", 
          "7216.32", 
          "STL-HB-100", 
          "450.50", 
          "Hundredweight (cwt)", // ⚡ FIX: Updated to match new format
          "10", 
          "Standard length hot-rolled beam", 
          "ASTM A992", 
          "Generic"
      ];

      const csvContent = [
          headers.join(","),
          sampleRow.join(",")
      ].join("\n");

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      
      link.setAttribute("href", url);
      link.setAttribute("download", "TariffBeater_Inventory_Template.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
  };

  const handleCsvUpload = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (evt) => {
          const text = evt.target.result;
          const rows = text.split('\n').map(r => r.trim()).filter(r => r.length > 0);
          if (rows.length < 2) return alert("CSV is empty or missing data rows.");
          
          const headers = rows[0].split(',').map(h => h.trim().toLowerCase());
          const requiredHeaders = ['name', 'hs_code', 'price'];
          const hasRequired = requiredHeaders.every(h => headers.includes(h));
          
          if (!hasRequired) return alert("Invalid Template. CSV must contain 'Name', 'HS_Code', and 'Price' columns.");

          const parsedRows = rows.slice(1).map((row, index) => {
              const values = row.split(',');
              const obj = headers.reduce((acc, header, i) => {
                  acc[header] = values[i]?.trim();
                  return acc;
              }, {});

              const chapter = obj.hs_code ? obj.hs_code.substring(0, 2) : '';
              const isConstruction = CONSTRUCTION_CHAPTERS.includes(chapter);
              const compliance = checkCompliance(obj.name + ' ' + (obj.material || ''), obj.hs_code);
              
              return {
                  id: index,
                  ...obj,
                  isValid: obj.name && obj.hs_code && obj.price && !compliance.restricted && isConstruction,
                  errorMsg: !isConstruction 
                    ? `Chapter ${chapter} restricted. TariffBeater allows Architectural/Structural materials only.` 
                    : compliance.restricted ? compliance.reason : (!obj.hs_code ? "Missing HS Code" : "")
              };
          });

          setCsvRows(parsedRows);
          setCsvValidated(true);
      };
      reader.readAsText(file);
      e.target.value = null; 
  };

  const handleBulkSubmit = async () => {
      const validRows = csvRows.filter(r => r.isValid);
      if (validRows.length === 0) return alert("No valid rows to import.");
      setSubmitting(true);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';
      let successCount = 0;

      for (const row of validRows) {
          const payload = {
              name: row.name,
              hs_code: row.hs_code,
              sku: row.sku || '',
              price_cad: parseFloat(row.price),
              uom: row.uom || 'Each (EA)', // ⚡ FIX: Updated Fallback
              moq: parseInt(row.moq) || 1,
              stock_status: 'draft', 
              origin_province: 'ON',
              description: row.description || '',
              material_grade: row.material || '',
              brand: row.brand || '',
              supplier_id: supplierId,
              is_civilian_compliant: true,
              is_safety_compliant: true
          };

          try {
             await fetch(`${apiUrl}/products`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
                body: JSON.stringify(payload)
             });
             successCount++;
          } catch (e) { console.error("Bulk insert failed for row", row); }
      }

      setSubmitting(false);
      alert(`✅ Successfully imported ${successCount} products as Drafts. You can now add images from your Inventory screen.`);
      handleCancel();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (complianceStatus.restricted) return alert(`⛔ BLOCKED: ${complianceStatus.reason}`);
    if (preFlightWarning) return alert(`⛔ BLOCKED: ${preFlightWarning.title}`);
    setSubmitting(true);

    if (!formData.name || !formData.price_cad || !formData.hsCode) {
      alert("Please fill in all required fields.");
      setSubmitting(false);
      return;
    }

    const finalUom = isCustomUom ? formData.custom_uom : formData.uom;
    if (isCustomUom && !finalUom) {
        alert("Please specify the Custom Unit of Measure.");
        setSubmitting(false);
        return;
    }

    let certificate_file_url = initialData?.certificate_file_url || null;
    if (complianceHold && certFile) {
        try {
            certificate_file_url = await uploadCertificate();
        } catch (uploadErr) {
            alert(uploadErr.message);
            setSubmitting(false);
            return;
        }
    } else if (complianceHold && !certFile) {
        alert("Please upload the required compliance document.");
        setSubmitting(false);
        return;
    }

    const finalCertString = certifications.length > 0 ? certifications.join(', ') : 'None / Standard';
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

    try {
      const finalImageUrls = await uploadFilesToSupabase(images, 'product-media');
      const finalPdfUrls = await uploadFilesToSupabase(pdfs, 'product-media');
      const finalCadUrls = await uploadFilesToSupabase(cads, 'product-media');

      const payload = {
        ...formData,
        hs_code: formData.hsCode, 
        supplier_id: supplierId,
        uom: finalUom, 
        certification_claimed: finalCertString, 
        price_cad: parseFloat(formData.price_cad),
        moq: parseInt(formData.moq) || 1, 
        lead_time_days: parseInt(formData.lead_time_days) || 0,
        stock_status: formData.stock_status,
        image_url: finalImageUrls,
        technical_docs_url: finalPdfUrls,
        cad_url: finalCadUrls,
        certificate_file_url 
      };
      delete payload.custom_uom;
      delete payload.is_safety_compliant; 

      let response;
      if (initialData && initialData.id) {
         response = await fetch(`${apiUrl}/products/${initialData.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
            body: JSON.stringify(payload)
         });
      } else {
         response = await fetch(`${apiUrl}/products`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
            body: JSON.stringify(payload)
         });
      }

      const result = await response.json();
      if (response.ok) {
        alert(`✅ Product ${initialData ? 'Updated' : 'Listed'} Successfully!`);
        handleCancel();
      } else {
        if (response.status === 400 && result.error && result.error.includes("Certification Required")) {
            setComplianceHold(true);
            setHoldReason(result.error);
            alert("⚠️ Regulatory Hold: Please upload the required compliance document to proceed.");
        } else {
            alert(`❌ Error: ${result.error || 'Failed to create listing'}`);
        }
      }
    } catch (err) {
      console.error("Submission Error:", err);
      alert("Network Error: Could not connect to server.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || rulesLoading) return (
    <div className="max-w-4xl mx-auto my-12 p-6 bg-white rounded-2xl shadow-lg animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
      <p className="text-center text-gray-500 mt-4">Loading Secure Environment...</p>
    </div>
  );

  if (currentUserId && !supplierId) {
    return <SupplierOnboarding userId={currentUserId} onProfileCreated={setSupplierId} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-5xl mx-auto">
        
        <div className="mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                {initialData ? t('inventory.edit_title') : t('inventory.add_title')}
            </h1>
            <p className="mt-2 text-sm text-gray-500">
                {initialData ? t('inventory.edit_desc') : t('inventory.add_sub')}
            </p>
          </div>
          
          {!initialData && (
              <div className="flex bg-white p-1 rounded-lg shadow-sm border border-gray-200">
                  <button 
                      onClick={() => setEntryMode('single')} 
                      className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${entryMode === 'single' ? 'bg-blue-600 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                      {t('inventory.single_item')}
                  </button>
                  {isPremium ? (
                      <button 
                          onClick={() => setEntryMode('bulk')} 
                          className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${entryMode === 'bulk' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-50'}`}
                      >
                          {t('inventory.bulk_csv')}
                      </button>
                  ) : (
                      <button 
                          onClick={() => alert(t('inventory.prem_feature'))} 
                          className="px-4 py-2 text-sm font-bold rounded-md transition-all text-gray-400 bg-gray-50 cursor-not-allowed flex items-center gap-2"
                          title="Premium Feature"
                      >
                          <span>🔒</span> {t('inventory.bulk_csv')}
                      </button>
                  )}
              </div>
          )}
        </div>

        {/* =========================================================
            MODE A: BULK CSV UPLOAD (STAGING & DRAFTS)
        ========================================================= */}
        {entryMode === 'bulk' && !initialData && (
             <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in">
                 <div className="p-8 border-b border-gray-100 bg-indigo-50/50">
                    <h2 className="text-xl font-bold text-gray-900 mb-2">{t('inventory.bulk_title')}</h2>
                    <p className="text-gray-600 text-sm mb-6">
                        <Trans i18nKey="inventory.bulk_desc">Upload up to 500 SKUs at once. Items will be saved as <strong>Drafts</strong> so you can attach specific CAD files and images later.</Trans>
                    </p>
                    <div className="flex flex-col md:flex-row gap-4 items-center">
                        <button onClick={downloadCsvTemplate} className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-bold text-gray-700 shadow-sm hover:bg-gray-50">
                            {t('inventory.dl_csv')}
                        </button>
                        <input type="file" accept=".csv" onChange={handleCsvUpload} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-bold file:bg-indigo-100 file:text-indigo-700 hover:file:bg-indigo-200 cursor-pointer" />
                    </div>
                 </div>
                 
                 {csvValidated && (
                     <div className="p-8">
                         <div className="flex justify-between items-center mb-4">
                             <h3 className="font-bold text-gray-800">{t('inventory.val_report')}</h3>
                             <span className="text-sm font-medium bg-gray-100 px-3 py-1 rounded-full">{csvRows.filter(r => r.isValid).length} Valid / {csvRows.length} Total</span>
                         </div>
                         <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                             <table className="min-w-full divide-y divide-gray-200 text-sm">
                                 <thead className="bg-gray-50 sticky top-0">
                                     <tr>
                                         <th className="px-4 py-3 text-left font-bold text-gray-500">{t('inventory.status')}</th>
                                         <th className="px-4 py-3 text-left font-bold text-gray-500">{t('inventory.name')}</th>
                                         <th className="px-4 py-3 text-left font-bold text-gray-500">{t('inventory.hs_code')}</th>
                                         <th className="px-4 py-3 text-left font-bold text-gray-500">{t('inventory.notes')}</th>
                                     </tr>
                                 </thead>
                                 <tbody className="bg-white divide-y divide-gray-100">
                                     {csvRows.map(row => (
                                         <tr key={row.id} className={row.isValid ? 'hover:bg-green-50' : 'bg-red-50/50'}>
                                             <td className="px-4 py-3">
                                                 {row.isValid ? <span className="text-green-600 font-bold">{t('inventory.ready')}</span> : <span className="text-red-600 font-bold">{t('inventory.blocked')}</span>}
                                             </td>
                                             <td className="px-4 py-3 font-medium text-gray-900">{row.name || t('inventory.missing')}</td>
                                             <td className="px-4 py-3 font-mono text-gray-600">{row.hs_code}</td>
                                             <td className="px-4 py-3 text-red-600 text-xs">{row.errorMsg}</td>
                                         </tr>
                                     ))}
                                 </tbody>
                             </table>
                         </div>
                         <div className="mt-6 flex justify-end gap-3">
                             <button onClick={() => {setCsvRows([]); setCsvValidated(false);}} className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">{t('inventory.reset')}</button>
                             <button onClick={handleBulkSubmit} disabled={submitting || csvRows.filter(r => r.isValid).length === 0} className="px-8 py-2 bg-indigo-600 text-white rounded font-bold shadow hover:bg-indigo-700 disabled:opacity-50">
                                 {submitting ? t('inventory.importing') : t('inventory.import_drafts', { count: csvRows.filter(r => r.isValid).length })}
                             </button>
                         </div>
                     </div>
                 )}
             </div>
        )}

        {/* =========================================================
            MODE B: SINGLE LISTING FORM 
        ========================================================= */}
        {entryMode === 'single' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fade-in">
                <div className="p-8 space-y-6">
                  
                  {/* ⚡ PRE-FLIGHT CHECKER UI */}
                  {preFlightWarning && !complianceStatus.restricted && (
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-5 mb-6 shadow-sm animate-fade-in-up">
                      <div className="flex items-start gap-3">
                        <span className="text-2xl mt-0.5">🎓</span>
                        <div>
                          <p className="text-sm text-amber-900 font-extrabold uppercase tracking-wide">{preFlightWarning.title}</p>
                          <p className="text-sm text-amber-800 font-bold mt-1">{preFlightWarning.message}</p>
                          <p className="text-xs text-amber-700 mt-2 bg-amber-100/50 p-2 rounded">{preFlightWarning.detail}</p>
                          <p className="text-xs text-amber-600 mt-2 font-bold">{t('inventory.preflight_action')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {complianceStatus.restricted && (
                    <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-4 shadow-sm animate-pulse">
                      <div className="flex">
                        <div className="ml-3">
                          <p className="text-sm text-red-800 font-extrabold uppercase tracking-wide">⛔ {t('inventory.block_title')}</p>
                          <p className="text-sm text-red-700 font-bold mt-1">{complianceStatus.reason}</p>
                          <p className="text-xs text-red-600 mt-2">{t('inventory.block_desc')}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* 1. CORE IDENTITY */}
                  <div>
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">{t('inventory.core_h3')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.product_name')} <span className="text-red-500">*</span></label>
                        <input 
                          type="text" required
                          className={`w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:bg-white transition-all ${complianceStatus.restricted || preFlightWarning ? 'border-red-300' : 'focus:border-blue-500'}`}
                          placeholder="e.g. Structural Steel H-Beam"
                          value={formData.name}
                          onChange={e => { setFormData({...formData, name: e.target.value}); resetHold(); }}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.mat_grade')}</label>
                        <input 
                            type="text"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white"
                            placeholder="e.g. ASTM A992 Steel"
                            value={formData.material_grade}
                            onChange={e => { setFormData({...formData, material_grade: e.target.value}); resetHold(); }}
                        />
                      </div>

                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.condition')}</label>
                          <select 
                              className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white"
                              value={formData.condition}
                              onChange={e => setFormData({...formData, condition: e.target.value})}
                          >
                              <option value="New">{t('inventory.cond_new')}</option>
                              <option value="Surplus">{t('inventory.cond_surplus')}</option>
                              <option value="Refurbished">{t('inventory.cond_refurb')}</option>
                          </select>
                      </div>

                      {/* ⚡ SMART HS CODE FIELD INCORPORATED HERE */}
                      <div className="relative" ref={dropdownRef}>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.hs_code')} <span className="text-red-500">*</span></label>
                        <input 
                          type="text" required
                          autoComplete="off"
                          className={`w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:bg-white transition-all ${complianceStatus.restricted || preFlightWarning ? 'border-red-500 bg-red-50 focus:border-red-500 text-red-700 font-bold' : 'focus:border-blue-500'}`}
                          placeholder="Search by keyword or exact HS Code (e.g. 7216.32)"
                          value={hsSearchTerm}
                          onChange={handleHsSearchChange}
                          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
                        />
                        
                        {showDropdown && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-blue-200 rounded-lg shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                                {isSearchingDb ? (
                                    <div className="p-3 text-sm text-gray-500 flex items-center gap-2">
                                        <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                        Searching global trade index...
                                    </div>
                                ) : (
                                    <ul>
                                        {suggestions.map((item, index) => (
                                            <li key={index} onClick={() => handleSuggestionClick(item)} className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 flex flex-col">
                                                <span className="text-sm font-bold text-gray-900 line-clamp-1">{item.description}</span>
                                                <span className="text-xs font-mono text-blue-700 mt-0.5">HS Code: {item.code}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}

                        {complianceStatus.restricted ? (
                          <p className="text-red-600 text-xs mt-1 font-bold">⛔ Blocked: {complianceStatus.reason}</p>
                        ) : preFlightWarning ? (
                          <p className="text-red-600 text-xs mt-1 font-bold">⛔ Blocked: Incorrect Category</p>
                        ) : (
                          <p className="text-gray-400 text-xs mt-1">Allowed: Digits and dots.</p>
                        )}
                      </div>

                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.sku')}</label>
                          <input 
                            type="text"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                            placeholder="e.g. STL-HB-1049-G90"
                            value={formData.sku}
                            onChange={e => setFormData({...formData, sku: e.target.value})}
                          />
                      </div>

                      <div className="md:col-span-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.brand')}</label>
                          <input 
                              type="text"
                              className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white"
                              placeholder="e.g. Local Mill or Generic"
                              value={formData.brand}
                              onChange={e => setFormData({...formData, brand: e.target.value})}
                          />
                      </div>
                    </div>
                  </div>

                  {/* 2. COMMERCIAL TERMS */}
                  <div className="border-t border-gray-100 pt-6">
                    <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">{t('inventory.comm_h3')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      
                      {/* Price */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.unit_price')} <span className="text-red-500">*</span></label>
                        <div className="relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 font-bold sm:text-sm">$</span>
                          </div>
                          <input 
                            type="text" required inputMode="decimal"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 pl-8 pr-12 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                            placeholder="0.00"
                            value={formData.price_cad}
                            onChange={handlePriceChange}
                            onKeyDown={blockInvalidChar}
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <span className="text-gray-400 sm:text-sm font-bold">CAD</span>
                          </div>
                        </div>
                      </div>

                      {/* ⚡ UoM Dropdown */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.unit')} <span className="text-red-500">*</span></label>
                        {!isCustomUom ? (
                          <select 
                              className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                              value={formData.uom}
                              onChange={handleUomChange}
                          >
                              <optgroup label="Weight & Mass (Metals & Cement)">
                                  <option value="Hundredweight (cwt)">Hundredweight (cwt)</option>
                                  <option value="Pounds (lbs)">Pounds (lbs)</option>
                                  <option value="Kilograms (kg)">Kilograms (kg)</option>
                                  <option value="Metric Tonnes (MT)">Metric Tonnes (MT)</option>
                                  <option value="Short Tons (ST)">Short Tons (ST)</option>
                              </optgroup>
                              <optgroup label="Length (Extrusions & Rebar)">
                                  <option value="Linear Feet (LF)">Linear Feet (LF)</option>
                                  <option value="Linear Meters (LM)">Linear Meters (LM)</option>
                              </optgroup>
                              <optgroup label="Area & Volume (Timber & Concrete)">
                                  <option value="Square Feet (SF)">Square Feet (SF)</option>
                                  <option value="Square Meters (SQM)">Square Meters (SQM)</option>
                                  <option value="Cubic Yards (CY)">Cubic Yards (CY)</option>
                                  <option value="Cubic Meters (CM)">Cubic Meters (CM)</option>
                                  <option value="Board Feet (BF)">Board Feet (BF)</option>
                              </optgroup>
                              <optgroup label="Count & Packaged Goods">
                                  <option value="Each (EA)">Each (EA)</option>
                                  <option value="Pieces (PCS)">Pieces (PCS)</option>
                                  <option value="Skids / Pallets">Skids / Pallets</option>
                                  <option value="Bundles">Bundles</option>
                                  <option value="Truckload (TL)">Truckload (TL)</option>
                              </optgroup>
                              <optgroup label="Custom">
                                  <option value="Other" className="font-bold bg-gray-100">{t('inventory.unit_other', 'Other (Custom)')}</option>
                              </optgroup>
                          </select>
                        ) : (
                          <div className="flex gap-2">
                              <input 
                                  type="text"
                                  className="w-full rounded-lg border-blue-500 bg-white p-3 text-sm focus:ring-2 focus:ring-blue-200"
                                  placeholder="e.g. Bundle, Crate"
                                  value={formData.custom_uom}
                                  onChange={e => setFormData({...formData, custom_uom: e.target.value})}
                                  autoFocus
                              />
                              <button 
                                  type="button"
                                  onClick={() => setIsCustomUom(false)}
                                  className="text-gray-400 hover:text-red-500 font-bold px-2"
                                  title="Cancel Custom"
                              >✕</button>
                          </div>
                        )}
                      </div>

                      {/* MOQ */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.moq')}</label>
                        <input 
                          type="text" inputMode="numeric"
                          className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                          placeholder="1"
                          value={formData.moq}
                          onChange={(e) => handleIntegerChange(e, 'moq')}
                          onKeyDown={blockInvalidChar}
                        />
                      </div>
                    </div>
                  </div>

                  {/* 3. DETAILS & COMPLIANCE */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.origin')}</label>
                        <select 
                          className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                          value={formData.origin_province}
                          onChange={e => setFormData({...formData, origin_province: e.target.value})}
                        >
                          {['ON', 'BC', 'AB', 'QC', 'NS', 'MB', 'SK', 'NB', 'PE', 'NL'].map(p => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* MULTI-TAG CERTIFICATIONS */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.certifications')}</label>
                        <div className="flex gap-2 mb-2">
                          <input 
                              type="text"
                              className="w-full rounded-lg border-gray-200 bg-gray-50 p-2.5 text-sm focus:border-blue-500 focus:bg-white transition-all"
                              placeholder="e.g. ISO 9001, ASME, CWB (Press Enter)"
                              value={certInput}
                              onChange={(e) => setCertInput(e.target.value)}
                              onKeyDown={handleAddCert}
                          />
                          <button 
                              type="button" 
                              onClick={handleAddCert}
                              className="bg-gray-800 text-white px-4 rounded-lg text-sm font-bold hover:bg-gray-700"
                          >{t('inventory.add_btn')}</button>
                        </div>
                        
                        {/* Render Tags */}
                        <div className="flex flex-wrap gap-2 mt-2">
                            {certifications.length === 0 && <span className="text-xs text-gray-400 italic">{t('inventory.none_spec')}</span>}
                            {certifications.map((cert, index) => (
                                <span key={index} className="inline-flex items-center bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded text-xs font-bold">
                                    {cert}
                                    <button 
                                      type="button" 
                                      onClick={() => handleRemoveCert(cert)}
                                      className="ml-1.5 text-blue-400 hover:text-blue-800 text-sm leading-none"
                                    >×</button>
                                </span>
                            ))}
                        </div>
                      </div>
                    </div>
       
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.tech_desc')}</label>
                      <textarea 
                        required rows={4}
                        className={`w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:bg-white transition-all ${complianceStatus.restricted || preFlightWarning ? 'border-red-300' : 'focus:border-blue-500'}`}
                        placeholder="Describe material grade (e.g. ASTM A992), dimensions, tolerances..."
                        value={formData.description}
                        onChange={handleDescriptionChange}
                      />
                    </div>
                  </div>

                  {/* 4. MEDIA & DOCUMENTATION */}
                  <div className="border-t border-gray-100 pt-6">
                    <div className="flex justify-between items-end mb-4 border-b border-gray-100 pb-2">
                        <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">{t('inventory.media_h3')}</h3>
                        {!isPremium && <span className="bg-yellow-100 text-yellow-800 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase">{t('inventory.std_tier')}</span>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        
                        {/* Images Dropzone */}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 hover:bg-white transition-colors relative">
                            <label className="block text-sm font-bold text-gray-800 mb-1">{t('inventory.prod_images')}</label>
                            <p className="text-xs text-gray-500 mb-3">{images.length} / {MAX_IMAGES} {t('inventory.uploaded')}</p>
                            <input type="file" multiple accept="image/*" onChange={(e) => handleFileDrop(e, 'image')} disabled={images.length >= MAX_IMAGES} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                            <div className="text-center py-4 pointer-events-none">
                                <span className="text-2xl block mb-2">📸</span>
                                <span className="text-blue-600 font-bold text-sm">{t('inventory.click_drop_img')}</span>
                            </div>
                            {images.length > 0 && (
                                <div className="flex gap-2 mt-2 relative z-10 flex-wrap">
                                    {images.map((img, idx) => (
                                        <div key={idx} className="bg-blue-100 text-blue-800 text-[10px] px-2 py-1 rounded truncate w-16 relative group">
                                            Img {idx+1}
                                            <button type="button" onClick={() => removeFile(idx, 'image')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100">×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* PDFs Dropzone */}
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 hover:bg-white transition-colors relative">
                            <label className="block text-sm font-bold text-gray-800 mb-1">{t('inventory.spec_sheets')}</label>
                            <p className="text-xs text-gray-500 mb-3">{pdfs.length} / {MAX_PDFS} {t('inventory.uploaded')}</p>
                            <input type="file" multiple accept="application/pdf" onChange={(e) => handleFileDrop(e, 'pdf')} disabled={pdfs.length >= MAX_PDFS} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                            <div className="text-center py-4 pointer-events-none">
                                <span className="text-2xl block mb-2">📄</span>
                                <span className="text-blue-600 font-bold text-sm">{t('inventory.click_drop_pdf')}</span>
                            </div>
                            {pdfs.length > 0 && (
                                <div className="flex gap-2 mt-2 relative z-10 flex-wrap">
                                    {pdfs.map((pdf, idx) => (
                                        <div key={idx} className="bg-gray-200 text-gray-800 text-[10px] px-2 py-1 rounded truncate w-20 relative group">
                                            Doc {idx+1}
                                            <button type="button" onClick={() => removeFile(idx, 'pdf')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100">×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* CAD / BIM Dropzone */}
                        <div className={`md:col-span-2 border-2 border-dashed rounded-lg p-4 relative ${isPremium ? 'border-gray-300 bg-gray-50 hover:bg-white' : 'border-gray-200 bg-gray-100 opacity-75'}`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <label className="block text-sm font-bold text-gray-800 mb-1">{t('inventory.cad_models')}</label>
                                    <p className="text-xs text-gray-500 mb-3">{t('inventory.cad_desc')}</p>
                                </div>
                                {!isPremium && <span className="bg-yellow-100 text-yellow-800 text-[10px] px-2 py-0.5 rounded uppercase font-bold">{t('inventory.pro_feature')}</span>}
                            </div>
                            
                            <input type="file" multiple accept=".step,.iges,.dwg,.rvt" onChange={(e) => handleFileDrop(e, 'cad')} disabled={!isPremium || cads.length >= MAX_CAD} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                            <div className="text-center py-4 pointer-events-none border border-dashed border-gray-300 mx-4 mb-2 bg-white rounded">
                                <span className="text-2xl block mb-2">🏗️</span>
                                <span className={`font-bold text-sm ${isPremium ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {isPremium ? t('inventory.up_to_cad', { max: MAX_CAD, count: cads.length }) : t('inventory.upgrade_cad')}
                                </span>
                            </div>
                            {cads.length > 0 && (
                                <div className="flex gap-2 mt-2 px-4 relative z-10 flex-wrap">
                                    {cads.map((cad, idx) => (
                                        <div key={idx} className="bg-indigo-100 text-indigo-800 text-[10px] px-2 py-1 rounded truncate w-24 relative group font-mono">
                                            Model {idx+1}
                                            <button type="button" onClick={() => removeFile(idx, 'cad')} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center opacity-0 group-hover:opacity-100">×</button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Video URL */}
                        <div className="md:col-span-2 mt-2">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">{t('inventory.video_url')}</label>
                          <input 
                            type="url"
                            className="w-full rounded-lg border-gray-200 bg-gray-50 p-3 text-sm focus:border-blue-500 focus:bg-white transition-all"
                            placeholder="https://www.youtube.com/watch?v=..."
                            value={formData.video_url}
                            onChange={e => setFormData({...formData, video_url: e.target.value})}
                          />
                        </div>

                    </div>
                  </div>

                  {/* ⚡ LAYER 3: DUAL-USE DOCUMENT WALL */}
                  {complianceHold && (
                    <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-5 mt-6 animate-fade-in shadow-inner">
                      <div className="flex items-start gap-4">
                        <span className="text-3xl">⚠️</span>
                        <div className="w-full">
                          <h3 className="font-bold text-amber-900 text-lg">{t('inventory.reg_hold')}</h3>
                          <p className="text-sm text-amber-800 mt-1 font-medium">{holdReason}</p>
                          
                          <div className="mt-4 bg-white p-4 rounded-md border border-amber-200">
                            <label className="block text-sm font-bold text-gray-800 mb-2">{t('inventory.upload_cert')}</label>
                            <input 
                              type="file" 
                              accept="application/pdf"
                              onChange={handleFileSelect}
                              className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-bold file:bg-amber-100 file:text-amber-800 hover:file:bg-amber-200 transition-colors"
                            />
                            {certFile && <p className="text-xs text-green-600 font-bold mt-2">{t('inventory.file_ready')}</p>}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ZERO TOLERANCE DECLARATION */}
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 space-y-3">
                    <div className="flex items-start gap-3">
                      <input 
                        type="checkbox" required
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={formData.is_civilian_compliant}
                        onChange={e => setFormData({...formData, is_civilian_compliant: e.target.checked})}
                      />
                      <label className="text-sm text-blue-900">
                        <Trans i18nKey="inventory.zero_tol_desc">
                          <strong>Zero Tolerance Scope:</strong> I certify that this item is <strong>Strictly Commercial</strong> and is <strong>NOT</strong> a Dual-Use Item (goods with potential military application). I understand that listing Dual-Use items will result in an immediate ban.
                        </Trans>
                      </label>
                    </div>

                    <div className="flex items-start gap-3">
                      <input 
                        type="checkbox" required
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        checked={formData.is_safety_compliant}
                        onChange={e => setFormData({...formData, is_safety_compliant: e.target.checked})}
                      />
                      <label className="text-sm text-blue-900">
                        <Trans i18nKey="inventory.safety_desc">
                          <strong>Safety Standards:</strong> I attest that this product meets <strong>Canadian safety standards</strong> (e.g. CSA, CRN, UL) required for safe operation.
                        </Trans>
                      </label>
                    </div>
                  </div>

                </div>
                
                {/* Actions Footer */}
                <div className="bg-gray-50 px-8 py-4 border-t border-gray-100 flex justify-end gap-3 mt-auto">
                  <button 
                    type="button" 
                    onClick={handleCancel}
                    className="px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-white transition-colors"
                  >
                    {t('inventory.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    disabled={submitting || uploadingCert || complianceStatus.restricted || preFlightWarning || !formData.is_civilian_compliant || !formData.is_safety_compliant || (complianceHold && !certFile)} 
                    className={`px-6 py-2.5 rounded-lg font-bold shadow-md transition-all 
                      ${complianceStatus.restricted || preFlightWarning || !formData.is_civilian_compliant || !formData.is_safety_compliant ?
                      'bg-gray-400 cursor-not-allowed opacity-50' : 
                      (complianceHold && !certFile) ? 'bg-amber-400 text-amber-900 cursor-not-allowed' :
                      'bg-blue-600 text-white hover:bg-blue-700'} 
                      disabled:opacity-50`}
                  >
                    {complianceStatus.restricted || preFlightWarning ? t('inventory.list_prohib') : 
                     uploadingCert ? t('inventory.upload_doc') :
                     submitting ? t('inventory.publishing') : 
                     complianceHold ? t('inventory.submit_doc_pub') : 
                     t('inventory.pub_listing')}
                  </button>
                </div>
              </form>
            </div>

            <div className="hidden lg:block space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="text-xl">💡</span> {t('inventory.tips_title')}
                </h3>
                <ul className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_1')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_2')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_3')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_4')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_5')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_6')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span><strong>Lead Time</strong> {t('inventory.tip_7')}</li>
                  <li className="flex items-start gap-2"><span className="text-green-500 font-bold">•</span>{t('inventory.tip_8')}</li>
                </ul>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}